import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Draggable, Droppable } from '@hello-pangea/dnd';
import { gsap } from 'gsap';
import update from 'immutability-helper';
import { useTranslation } from 'react-i18next';
import { Transition } from 'react-transition-group';

import { ReactComponent as MoveOutIcon } from '@pkg/assets/icons/v2/arrow-corner-down.svg';
import { ReactComponent as CloseIcon } from '@pkg/assets/icons/v2/close.svg';
import { CircleButton } from '@pkg/components';
import { useAppDispatch, useAppSelector } from '@pkg/hooks';

import { type SidebarGroup, type SidebarYat } from '@app/helpers';
import { setSidebarGroups, storeDashboard } from '@app/store/actions/dashboard';
import { useEmojisStore } from '@app/store/zustand/emojis/useEmojisStore';

import YatSidebarItem from '../Item';

import './Group.scss';
import YatSidebarGroupThumb from './GroupThumb';

interface YatSidebarGroupProps {
    group: SidebarGroup;
    index: string;
    isUpdateDisabled?: boolean;
    isPrimaryYatPicker?: boolean;

    onClick?(): void;
}

const YatSidebarGroup = ({
    group,
    index,
    isUpdateDisabled,
    onClick,
    isPrimaryYatPicker = false,
}: YatSidebarGroupProps) => {
    const { t } = useTranslation(['dashboard']);
    const dispatch = useAppDispatch();
    const currentEmoji = useEmojisStore((state) => state.currentEmoji);
    const { sidebar } = useAppSelector((state) => state.dashboard);
    // TODO: move this to redux
    const [hasActiveItem, setHasActiveItem] = useState<boolean>(false);
    const [groupTitle, setGroupTitle] = useState<string>(group.name || '');
    const containerRef = useRef<HTMLDivElement>(null);
    const groupRef = useRef<HTMLDivElement>(null);
    const thumbRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (group) {
            setHasActiveItem(group.includes(currentEmoji));
        }
    }, [currentEmoji, group]);

    const setIsOpen = useCallback(
        (openState: boolean) => {
            const groupIndex = Number(index);
            const sidebarState = sidebar;
            (sidebarState.children[groupIndex] as SidebarGroup).open = openState;
            dispatch(setSidebarGroups(sidebarState));
        },
        [dispatch, sidebar, index],
    );

    useEffect(() => {
        const listener = (event) => {
            // Do nothing if clicking ref's element or descendent elements
            if (event.target.closest('.yat-sidebar-group') != null) {
                return;
            }
            setIsOpen(false);
        };

        if (group.open) {
            document.addEventListener('mousedown', listener);
            document.addEventListener('touchstart', listener);
        }
        return () => {
            document.removeEventListener('mousedown', listener);
            document.removeEventListener('touchstart', listener);
        };
    }, [sidebar, containerRef, group.open, setIsOpen]);

    const AnimationOnEntering = () => {
        const thumb = thumbRef.current;
        const group = groupRef.current;

        if (thumb && group) {
            const thumbSize = parseInt(getComputedStyle(thumb.children[0]).getPropertyValue('width'), 10);
            gsap.timeline()
                .set(thumb.children[0], { opacity: 1 })
                .to(thumb?.children[0].children, { opacity: 0, duration: 0.15 })
                .set(thumb, { display: 'none' })
                .set(group, { display: 'block' })
                .from(group, { width: thumbSize, height: thumbSize, duration: 0.5, ease: 'power4.out' })
                .fromTo(group.children[1], { opacity: 0 }, { opacity: 1, duration: 0.25 }, '-=0.3')
                .set(group, { width: 'auto', height: 'auto' });
        }
    };

    const animationOnExiting = () => {
        const group = groupRef.current;
        const thumb = thumbRef.current;

        if (group && thumb) {
            const thumbSize = parseInt(getComputedStyle(thumb.children[0]).getPropertyValue('width'), 10);
            gsap.timeline()
                .to(group.children[1], { opacity: 0, duration: 0.25 })
                .to(group, { width: thumbSize, height: thumbSize, duration: 0.35, ease: 'power4.out' }, '-=0.1')
                .set(group, { display: 'none', width: 'auto', height: 'auto' })
                .set(thumb, { display: 'block' })
                .to(thumb?.children[0].children, { opacity: 1, duration: 0.15 });
        }
    };

    const moveYatOutside = useCallback(
        (yat: SidebarYat, itemIndex: number) => {
            const groupIndex = Number(index);
            const newIndex = groupIndex + 1;
            dispatch(
                storeDashboard(
                    update(sidebar, {
                        children: {
                            [groupIndex]: {
                                children: {
                                    $splice: [[itemIndex, 1]], // Remove from group
                                },
                            },
                            $splice: [[newIndex, 0, yat]], // Place Yat outside and after the group
                        },
                    }),
                ),
            );
        },
        [dispatch, sidebar, index],
    );

    // Remove group if there's less than 2 Yats inside
    useEffect(() => {
        if (group.length > 1) return;
        const groupIndex = Number(index);
        dispatch(
            storeDashboard(
                update(sidebar, {
                    children: {
                        $splice: [
                            [groupIndex, 1], // remove group
                            [groupIndex, 0, group.children[0]], // Place the last child Yat on sidebar root level
                        ],
                    },
                }),
            ),
        );
    }, [dispatch, group, sidebar, index]);

    const updateGroupTitle = useCallback(() => {
        if (group.parent && groupTitle.length < 50) {
            group.name = groupTitle;
            const groupIndex = Number(index);
            dispatch(
                storeDashboard(
                    update(sidebar, {
                        children: {
                            [groupIndex]: {
                                name: { $set: groupTitle },
                            },
                        },
                    }),
                ),
            );
        }
    }, [dispatch, index, group, sidebar, groupTitle]);

    const hideTitleInput = isUpdateDisabled && groupTitle.length === 0;

    return (
        <Transition
            nodeRef={containerRef}
            in={group.open}
            timeout={650}
            onEntering={AnimationOnEntering}
            onExiting={animationOnExiting}
        >
            <div ref={containerRef} className="v2-global-reset yat-sidebar-group-outer">
                <div ref={groupRef} className="yat-sidebar-group" style={{ display: 'none' }}>
                    <div className="yat-sidebar-group__background" />
                    <div className="yat-sidebar-group__inner">
                        <CircleButton
                            size="small"
                            ariaLabel="close"
                            onClick={() => setIsOpen(false)}
                            classes="yat-sidebar-group__close"
                        >
                            <CloseIcon />
                        </CircleButton>
                        {!hideTitleInput && (
                            <input
                                disabled={isUpdateDisabled}
                                placeholder={t('group_title')}
                                className="yat-sidebar-group__title-input"
                                size={1}
                                value={groupTitle}
                                maxLength={50}
                                spellCheck="false"
                                autoComplete="off"
                                onChange={(e) => setGroupTitle(e.target.value)}
                                onBlur={updateGroupTitle}
                            />
                        )}
                        {group.open && (
                            <Droppable droppableId={'droppableSidebarGroup' + index} type={'GROUP_' + index}>
                                {(provided) => (
                                    <ul
                                        ref={provided.innerRef}
                                        {...provided.droppableProps}
                                        className="yat-sidebar-group__list"
                                    >
                                        {group.children.map((yat, i) => (
                                            <Draggable
                                                key={'Group' + index + 'Item' + i}
                                                draggableId={'Group' + index + 'Item' + i}
                                                index={i}
                                                isDragDisabled={isUpdateDisabled}
                                            >
                                                {(provided) => (
                                                    <li
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                        className="yat-sidebar-group__item"
                                                    >
                                                        <YatSidebarItem
                                                            onClick={onClick}
                                                            yat={yat as SidebarYat}
                                                            isActive={currentEmoji === yat.toString()}
                                                            isPrimaryYatPicker={isPrimaryYatPicker}
                                                        />
                                                        {!isUpdateDisabled && (
                                                            <CircleButton
                                                                size="small"
                                                                ariaLabel={t('move_yat_out_of_the_group', {
                                                                    yat: yat.toString(),
                                                                })}
                                                                onClick={() => moveYatOutside(yat as SidebarYat, i)}
                                                                classes="yat-sidebar-group__item-remove-button"
                                                            >
                                                                <MoveOutIcon aria-hidden="true" />
                                                            </CircleButton>
                                                        )}
                                                    </li>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </ul>
                                )}
                            </Droppable>
                        )}
                    </div>
                </div>
                <div ref={thumbRef}>
                    <YatSidebarGroupThumb group={group} isActive={hasActiveItem} onClick={() => setIsOpen(true)} />
                </div>
            </div>
        </Transition>
    );
};

export default YatSidebarGroup;
